<template>

    <v-card v-if="mapping" :class="!hasErrors ? 'successBorder' : 'errorBorder'">

        <v-card-text class="text-left pt-0 pb-0 pl-2">

            <v-container fluid>
                <v-row no-gutters>
                    <v-col cols="5" class="pt-5" @click="columnNameClicked">

                        <v-badge overlap v-if="isConfirmed" left color="green darken-1">
                            <template v-slot:badge>
                                <v-icon>mdi-check</v-icon>
                            </template>
                            <v-chip color="blue darken-3 white--text" @click.stop="debugClicked">
                                {{ columnLetter }}
                            </v-chip>
                        </v-badge>

                        <v-badge overlap v-if="isIgnored" left color="red darken-1">
                            <template v-slot:badge>
                                <v-icon>mdi-close</v-icon>
                            </template>
                            <v-chip color="blue darken-3 white--text" @click.stop="debugClicked">
                                {{ columnLetter }}
                            </v-chip>
                        </v-badge>

                        <v-chip v-if="!isConfirmed && ! isIgnored" color="blue darken-3 white--text"
                                @click.stop="debugClicked">
                            {{ columnLetter }}
                        </v-chip>

                        <span class="columnName subtitle-1 pl-3">{{ columnName }}</span>

                    </v-col>

                    <!-- DATA VARIABLE AND OPTIONS SELECTIONS -->
                    <v-col cols="7">

                        <v-row align-content-center>

                            <!-- ARROW DATA VARIABLE -->
                            <v-col sm="2">
                                <v-icon v-if="!isIgnored"
                                        :class="!dataVariableSelectModel ? 'mappingArrowAnimated' : ''"
                                        x-large
                                        :color="!dataVariableSelectModel ? 'primary' : 'grey'">
                                    mdi-chevron-right
                                </v-icon>
                            </v-col>

                            <!-- DATA VARIABLE PICKER -->
                            <v-col sm="9">

                                <v-combobox v-if="!isIgnored"
                                            v-model="dataVariableSelectModel"
                                            class="pt-0"
                                            hint="Map to variable"
                                            :items="variablesAvailable"
                                            item-text="name"
                                            :clearable="true"
                                            label="Select"
                                            persistent-hint
                                            return-object
                                            single-line
                                            @change="variableMapped"

                                >
                                    <template v-slot:item="{ index, item }">
                                        <div class="dataVariable">
                                            <div class="dataVariableLabel">
                                                <data-variable-chip class="pr-2" :type="item.type"></data-variable-chip>
                                            </div>
                                            <div class="dataVariableName">
                                                {{ item.name }}
                                            </div>
                                        </div>
                                    </template>
                                </v-combobox>

                            </v-col>

                            <!-- SETTINGS BUTTON -->
                            <v-col sm="1" class="text-center">
                                <v-btn icon
                                       v-show="(isConfirmed || isIgnored)"
                                       :fab="isConfirmed || isIgnored"
                                       :text="isConfirmed || isIgnored"
                                       @click="settingsButtonClicked"
                                       :color="isConfirmed || isIgnored ? 'grey' : 'primary'"
                                       small>
                                    <v-icon>
                                        mdi-cog
                                    </v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>

                        <v-row align-content-center v-if="isMultiple">

                            <!-- DATA OPTION ARROW -->
                            <v-col sm="2" v-if="isMultiple && !isIgnored">
                                <v-icon v-if="!isIgnored"
                                        :class="!optionSelectedModel ? 'mappingArrowAnimated' : ''"
                                        x-large
                                        :color="!optionSelectedModel ? 'primary' : 'grey'">
                                    mdi-chevron-right
                                </v-icon>
                            </v-col>

                            <!-- Show option selected if one found -->
                            <v-col sm="9" v-if="isMultiple && !isIgnored">

                                <!-- Non confirmed state -->
                                <v-select
                                        class="pt-0 pl-0"
                                        :items="optionsAvailable"
                                        v-model="optionSelectedModel"
                                        label="Select"
                                        single-line
                                        menu-props="bottom"
                                        :persistent-hint="true"
                                        hint="Map to data variable option"
                                        item-text="name"
                                        item-value="id"
                                        @change="optionMapped"
                                        return-object
                                        clearable
                                ></v-select>

                            </v-col>

                            <!-- SPACER -->
                            <v-col sm="1" v-if="isMultiple && !isIgnored">
                            </v-col>

                        </v-row>
                    </v-col>

                    <!-- COLUMN STATS EXPANSION DRAWER -->
                    <v-col sm="12" v-if="isStatsVisible">
                        <v-divider class="mt-3"></v-divider>

                        <csv-column-stats
                                v-bind="mapping.column.metadata"
                                :replace-with-null="mapping.column.userActions.skipValueErrors"
                        ></csv-column-stats>

                    </v-col>

                    <v-col sm="12" v-if="debug">
                        <json :data="mappingConfig"></json>
                    </v-col>

                    <v-col sm="12" v-show="isButtonsVisible">
                        <v-divider class="mt-3"></v-divider>

                        <v-btn v-if="!isIgnored"
                               class="ml-3 mt-3"
                               small
                               color="primary"
                               :disabled="isConfirmDisabled || !dataVariableSelectModel"
                               @click.stop="confirmClicked"
                        >
                            Confirm mapping
                        </v-btn>

                        <v-btn v-if="isReplaceVisible"
                               class="ml-3 mt-3"
                               @click.stop="replaceClicked"
                               small
                               color="primary">
                            Replace errors with null
                        </v-btn>

                        <v-btn v-if="isIgnoreVisible" class="ml-3 mt-3"
                               @click.stop="ignoreClicked"
                               small
                               color="primary">
                            Ignore this column
                        </v-btn>
                    </v-col>
                </v-row>
            </v-container>
        </v-card-text>
    </v-card>

</template>

<script>

    import {mapState, mapMutations, mapGetters, mapActions} from 'vuex';
    import CsvColumnStats from './ColumnStats.vue';
    import Json from 'vue-json-pretty';
    import DataVariablePicker from '../../../components/UI/DataVariablePicker';
    import DataVariableChip from '../../../components/UI/DataVariableChip';

    const vuexModule = 'PanelistImporter';

    const REQUIRED = ['4b9KWeVNAB0cEG4evmUpUT', '6srod1F590JD0u1iGjwsW', '7ISQaKgQBQoBRNGobStY9m'];

    export default {

        components: {
            CsvColumnStats,
            Json,
            DataVariablePicker,
            DataVariableChip
        },

        props: {
            mappingConfig: {
                type: Object,
                required: true,
            },
            dataVariables: {
                type: Array,
                required: true,
            }
        },

        data() {
            return {
                debug: false,
                optionSelectedModel: null,
                mapping: null,
                dataVariableSelectModel: null,
            }
        },

        computed: {

            ...mapGetters(vuexModule, [
                'mappedDataVariableIds',
                'dataVariablesAvailable'
            ]),

            ...mapState(vuexModule, [
                'mappings',
                'rawMappingTable',
            ]),

            isRequired() {
                if (this.dataVariableSelectModel === null) return false;
                if (REQUIRED.includes(this.dataVariableSelectModel.uuid)) return true;
                return false;
            },
            isMultiple() {
                return this.dataVariableSelectModel && this.dataVariableSelectModel.type === 'checkbox';
            },
            columnName() {
                return this.mapping.column.name + (this.mapping.column.optionName ? ' (' + this.mapping.column.optionName + ')' : '');
            },
            optionsAvailable() {
                return this.dataVariableSelectModel && this.dataVariableSelectModel.options ? this.dataVariableSelectModel.options : [];
            },
            variablesAvailable() {
                return this.dataVariablesAvailable(this.mapping.column.metadata.possibleTypes);
            },
            hasErrors() {
                return this.mapping.column.metadata.errorCount > 0;
            },
            isIgnored() {
                return this.mapping.column.userActions.ignore;
            },
            isConfirmed() {
                return this.mapping.column.userActions.confirmed;
            },
            isReady() {
                return !this.mapping.column.userActions.confirmed &&
                    !this.mapping.column.userActions.ignore;
            },
            isButtonsVisible() {
                return !this.mapping.column.userActions.confirmed &&
                    !this.mapping.column.userActions.ignore;
            },
            isConfirmDisabled() {

                if (this.isRequired) return false;

                if (this.mapping.column.metadata.errorCount > 0 &&
                    !this.mapping.column.userActions.skipValueErrors) {
                    return true;
                }
                if (this.dataVariableSelectModel) {
                    if (this.dataVariableSelectModel.type === "checkbox" && this.optionSelectedModel === null) return true;
                }
                return false;
            },
            isReplaceVisible() {
                if (this.isRequired) return false;
                if (this.mapping.column.userActions.skipValueErrors) return false;
                if (this.mapping.column.metadata.errorCount > 0) return true;
                return false;
            },
            isIgnoreVisible() {
                if (!this.isRequired) return true;
                if (this.mapping.column.userActions.skipValueErrors) return false;
                //if (this.mapping.column.metadata.errorCount > 0) return true;
                return false;
            },
            isStatsVisible() {
                if (!this.dataVariableSelectModel) return false;
                if (!this.mapping.column.userActions.confirmed && !this.mapping.column.userActions.ignore) return true;
            },
            columnLetter() {
                return this.mapping.column.index + 1;
            }
        },

        methods: {

            variableMapped(variable) {
                this.mapping.variable.id = variable ? variable.id : null;
                this.mapping.variable.name = variable ? variable.name : null;
                this.mapping.variable.type = variable ? variable.type : null;
                this.mapping.variable.optionId = null;
                this.mapping.variable.optionName = null;
                this.mapping.column.userActions.confirmed = false;
                this.$emit('change', Object.assign({}, this.mapping));
            },

            optionMapped(option) {
                this.mapping.variable.optionId = option ? option.id : null;
                this.mapping.variable.optionName = option ? option.name : null;
                this.$emit('change', Object.assign({}, this.mapping));
            },

            confirmClicked() {
                this.mapping.column.userActions.confirmed = true;
                this.$emit('config', Object.assign({}, this.mapping));
            },

            ignoreClicked() {
                this.mapping.column.userActions.ignore = true;
                this.$emit('config', Object.assign({}, this.mapping));
            },

            replaceClicked() {
                this.mapping.column.userActions.skipValueErrors = true;
                this.$emit('config', Object.assign({}, this.mapping));
            },

            columnNameClicked() {
                this.mapping.column.userActions.confirmed = false;
                this.mapping.column.userActions.ignore = false;
                this.$emit('change', Object.assign({}, this.mapping));
            },

            settingsButtonClicked() {
                this.mapping.column.userActions.confirmed = false;
                this.mapping.column.userActions.ignore = false;
                this.mapping.column.userActions.skipValueErrors = false;
                this.$emit('config', Object.assign({}, this.mapping));
            },

            debugClicked() {
                if (this.hasRole('Super Admin')) this.debug = !this.debug;
            }
        },

        mounted() {
            this.mapping = Object.assign(this.mappingConfig);

            // Select auto mapped data point
            if (this.mapping.variable.id) {
                const dataVariable = Object.assign({}, this.dataVariables.find(dataVariable => dataVariable.id === this.mapping.variable.id));

                this.dataVariableSelectModel = dataVariable;

                // Select auto mapped choice
                if (dataVariable.type === "checkbox") {
                    if (this.mapping.variable.optionId) {
                        this.optionSelectedModel = dataVariable.options.find((option) => option.id === this.mapping.variable.optionId);

                        // Confirm no error mapping automatically
                        if (this.mapping.column.metadata.errorCount === 0) {
                            this.mapping.column.userActions.confirmed = true;
                        }
                    }
                } else {
                    // Confirm no error mapping automatically
                    if (this.mapping.column.metadata.errorCount === 0) {
                        this.mapping.column.userActions.confirmed = true;
                    }
                }
            }
        },
    }
</script>

<style scoped>

    .columnName {
        cursor: pointer;
    }

    .successBorder {
        border-left: 5px solid green;
    }

    .errorBorder {
        border-left: 5px solid red;
    }

    .mappingArrowAnimated {
        animation-duration: 1s;
        animation-name: slidein;
        animation-iteration-count: infinite;
    }

    .dataVariable {
        display: flex;
        align-items: center;
    }

    .dataVariableLabel {
        width: 120px;
    }

    .dataVariableName {

    }

    @keyframes slidein {
        from {
            margin-left: 0%;
            width: 100%;
        }

        50% {
            margin-left: 30%;
            width: 100%;
        }

        to {
            margin-left: 0%;
            width: 100%;
        }
    }
</style>
